import { useCallback, useEffect, useRef } from 'react'
import { INTERACTION } from '@/lib/theme/tokens'

type PressActionOptions = {
  disabled?: boolean
  lockMs?: number
  maxAsyncLockMs?: number
}

/**
 * Single-flight guard for tap-driven commands.
 *
 * This is intentionally separate from `Button`: many navigation surfaces are
 * cards, map pins, or rows. They need the same "one tap, one route mutation"
 * contract so a double tap cannot stack duplicate screens while the first
 * navigation/action is still resolving.
 */
export function usePressAction<TArgs extends unknown[]>(
  action: (...args: TArgs) => void | Promise<unknown>,
  options: PressActionOptions = {},
) {
  const {
    disabled = false,
    lockMs = INTERACTION.pressLockMs,
    maxAsyncLockMs = INTERACTION.maxAsyncPressLockMs,
  } = options
  const lockedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const circuitBreakerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (circuitBreakerRef.current) clearTimeout(circuitBreakerRef.current)
    timerRef.current = null
    circuitBreakerRef.current = null
  }, [])

  const unlock = useCallback(() => {
    clearTimers()
    lockedRef.current = false
  }, [clearTimers])

  const release = useCallback(() => {
    clearTimers()
    timerRef.current = setTimeout(() => {
      lockedRef.current = false
      timerRef.current = null
    }, lockMs)
  }, [clearTimers, lockMs])

  useEffect(() => {
    return () => {
      clearTimers()
    }
  }, [clearTimers])

  return useCallback(
    (...args: TArgs) => {
      if (disabled || lockedRef.current) return
      lockedRef.current = true

      let result: void | Promise<unknown>
      try {
        result = action(...args)
      } catch (error) {
        unlock()
        throw error
      }

      if (result && typeof result.finally === 'function') {
        circuitBreakerRef.current = setTimeout(unlock, maxAsyncLockMs)
        void result.finally(release)
        return
      }

      release()
    },
    [action, disabled, maxAsyncLockMs, release, unlock],
  )
}
